import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2, LogIn } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { guestOpacApi, type GuestOpacResourceDetail } from "@/lib/api/guest-opac";
import { GuestCatalogLayout } from "@/components/guest-catalog/GuestCatalogLayout";
import { GuestCatalogRelated } from "@/components/guest-catalog/GuestCatalogRelated";
import { GuestDigitalResourcePanel } from "@/components/guest-catalog/GuestDigitalResourcePanel";
import { GuestResourceSubjects } from "@/components/guest-catalog/GuestResourceSubjects";
import { useGuestOpacTenantSlug } from "@/hooks/useGuestOpacTenantSlug";
import { useGuestOpacBranding } from "@/hooks/useGuestOpacBranding";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export function GuestResourceDetailPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { isLoading: tenantLoading } = useTenant();
  const tenantSlug = useGuestOpacTenantSlug();
  const branding = useGuestOpacBranding(tenantSlug);
  const [resource, setResource] = useState<GuestOpacResourceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantLoading) return;
    if (!publicId || !tenantSlug) {
      setLoading(false);
      if (!tenantSlug) setError("Library catalog is unavailable on this site.");
      return;
    }
    setLoading(true);
    setError(null);
    guestOpacApi
      .getResource(publicId, tenantSlug)
      .then(setResource)
      .catch(() => setError("Resource not found or unavailable."))
      .finally(() => setLoading(false));
  }, [publicId, tenantSlug, tenantLoading]);

  const relatedTerm = resource?.subjects?.[0] ?? resource?.authors?.[0];

  return (
    <GuestCatalogLayout libraryName={branding.libraryName} logoUrl={branding.logoUrl}>
      <Button variant="ghost" size="sm" className="mb-4 -ml-2" onClick={() => navigate("/opac")}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to catalog
      </Button>

      {tenantLoading || branding.isLoading || loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error || !resource ? (
        <p className="py-16 text-center text-sm text-gray-500">{error ?? "Resource not found."}</p>
      ) : (
        <div className="space-y-10">
          <div className="grid gap-8 lg:grid-cols-[min(280px,100%)_1fr]">
            <div className="mx-auto w-full max-w-[280px] overflow-hidden rounded-lg bg-muted lg:mx-0">
              {resource.coverUrl ? (
                <img src={resource.coverUrl} alt="" className="aspect-[3/4] w-full object-cover" />
              ) : (
                <div className="flex aspect-[3/4] items-center justify-center text-muted-foreground">
                  <BookOpen className="h-16 w-16 opacity-40" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Badge variant="secondary" className="mb-2 capitalize">
                  {resource.type}
                </Badge>
                <h1 className="text-2xl font-bold text-gray-900 lg:text-3xl">{resource.title}</h1>
                {resource.authors?.length > 0 && (
                  <p className="mt-2 text-gray-600">{resource.authors.join(", ")}</p>
                )}
              </div>

              <Card>
                <CardContent className="grid gap-3 p-4 text-sm sm:grid-cols-2">
                  {resource.publisher && (
                    <div>
                      <span className="font-medium text-gray-700">Publisher</span>
                      <p className="text-gray-600">{resource.publisher}</p>
                    </div>
                  )}
                  {resource.publishedDate && (
                    <div>
                      <span className="font-medium text-gray-700">Published</span>
                      <p className="text-gray-600">{resource.publishedDate}</p>
                    </div>
                  )}
                  {resource.bindingType && (
                    <div>
                      <span className="font-medium text-gray-700">Binding</span>
                      <p className="text-gray-600">{resource.bindingType}</p>
                    </div>
                  )}
                  {resource.physicalDescription && (
                    <div className="sm:col-span-2">
                      <span className="font-medium text-gray-700">Physical description</span>
                      <p className="text-gray-600">{resource.physicalDescription}</p>
                    </div>
                  )}
                  {resource.callNumber && (
                    <div>
                      <span className="font-medium text-gray-700">Call number</span>
                      <p className="text-gray-600">{resource.callNumber}</p>
                    </div>
                  )}
                  {resource.isbn && (
                    <div>
                      <span className="font-medium text-gray-700">ISBN</span>
                      <p className="text-gray-600">{resource.isbn}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {resource.subjects && resource.subjects.length > 0 && (
                <GuestResourceSubjects subjects={resource.subjects} />
              )}

              {resource.description && (
                <div>
                  <h2 className="mb-2 text-sm font-semibold text-gray-900">Description</h2>
                  <p className="text-sm leading-relaxed text-gray-600">{resource.description}</p>
                </div>
              )}

              {resource.availability && resource.type !== "digital" && (
                <div>
                  <h2 className="mb-2 text-sm font-semibold text-gray-900">Availability</h2>
                  <p
                    className={cn(
                      "text-sm font-medium",
                      resource.availability.availableCopies > 0
                        ? "text-emerald-700"
                        : "text-gray-500"
                    )}
                  >
                    {resource.availability.availableCopies > 0
                      ? `${resource.availability.availableCopies} of ${resource.availability.totalCopies} copies available`
                      : "No copies available right now"}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                {resource.type === "digital" && publicId && tenantSlug ? (
                  <GuestDigitalResourcePanel
                    publicId={publicId}
                    tenantSlug={tenantSlug}
                    title={resource.title}
                    digitalType={resource.digitalType}
                  />
                ) : resource.type !== "digital" ? (
                  <Button asChild>
                    <Link to={`/login?returnUrl=${encodeURIComponent(`/opac/view/${publicId}`)}`}>
                      <LogIn className="mr-2 h-4 w-4" />
                      Sign in to borrow or hold
                    </Link>
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {tenantSlug && publicId && relatedTerm && (
            <GuestCatalogRelated
              tenantSlug={tenantSlug}
              excludePublicId={publicId}
              searchTerm={relatedTerm}
            />
          )}
        </div>
      )}
    </GuestCatalogLayout>
  );
}
